import React from "react";
import { getForm1099BoxFilterValueLabel } from "@common/constants/domains/accounting/form1099Box";
import { uniq } from "@common/helpers/collections";
import { displayAccountLabel } from "@common/helpers/accounting";
import { ClassFilterType } from "@common/types/class";
import { FiltersTypes } from "@common/types/filters/general";
import { useChartOfAccountsQuery } from "@api/accountingReports.api";
import { useClasses } from "@api/class.api";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { ClassFilterLabel } from "./ClassFilterLabel";
import { formatSelectionSummary } from "./selectionSummaryLabel";

interface FiltersProps<Key extends keyof FiltersTypes> {
    filters: Pick<FiltersTypes, Key>;
}

export const Form1099BoxSummaryLabel: React.FC<
    FiltersProps<"form1099Boxes">
> = ({ filters: { form1099Boxes = [] } }) => {
    if (form1099Boxes.length === 0) {
        return null;
    }
    return (
        <>
            {formatSelectionSummary({
                selectedCount: form1099Boxes.length,
                singleLabel: getForm1099BoxFilterValueLabel(form1099Boxes[0]),
                noun: "1099 box",
            })}
        </>
    );
};

export const ClassSummaryLabel: React.FC<
    FiltersProps<"classes" | "classFilterType">
> = ({ filters }) => {
    const { classesMap } = useClasses();
    const { classes: classIds = [], classFilterType } = filters;

    if (
        classIds.length === 0 ||
        classFilterType === ClassFilterType.NO_CLASSES ||
        classFilterType === ClassFilterType.HAS_CLASSES
    ) {
        return <ClassFilterLabel filters={filters} />;
    }

    const summary = formatSelectionSummary({
        selectedCount: classIds.length,
        singleLabel: classesMap.get(classIds[0])?.label,
        noun: "class",
    });
    return (
        <>
            {classFilterType === ClassFilterType.EXCLUDE_SELECTED
                ? `Exclude: ${summary}`
                : summary}
        </>
    );
};

interface AccountSummaryLabelProps {
    accountIds: string[] | undefined;
    noun: string;
}

export const AccountSummaryLabel: React.FC<AccountSummaryLabelProps> = ({
    accountIds = [],
    noun,
}) => {
    const { data: chartOfAccounts, isLoading } = useChartOfAccountsQuery();
    const showAccountNumbers = useShowAccountNumbers();

    if (accountIds.length === 0) {
        return null;
    }

    if (isLoading) {
        return <>Loading…</>;
    }

    const accountsById = new Map(
        (chartOfAccounts?.accounts ?? []).map((account) => [
            account.id,
            displayAccountLabel(account, showAccountNumbers),
        ]),
    );
    const distinctAccounts = uniq(
        accountIds.map((accountId) => accountsById.get(accountId) ?? accountId),
    );
    const [firstAccountId] = accountIds;

    return (
        <>
            {formatSelectionSummary({
                selectedCount: distinctAccounts.length,
                singleLabel: accountsById.get(firstAccountId),
                noun,
            })}
        </>
    );
};
